import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { concernDetails } from "@/mocks/concernDetails";

type Props = {
  concernId: string | null;
  onClose: () => void;
  onOpenConsult: () => void;
};

export default function ConcernDetailOverlay({ concernId, onClose, onOpenConsult }: Props) {
  const detail = concernId ? concernDetails[concernId] : null;

  const [mounted, setMounted] = useState<boolean>(!!detail);
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const closingRef = useRef(false);

  useEffect(() => {
    if (detail) setMounted(true);
  }, [detail]);

  // lock body scroll
  useEffect(() => {
    if (!mounted) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [mounted]);

  // enter animation
  useLayoutEffect(() => {
    if (!mounted || !detail) return;
    const overlay = overlayRef.current;
    const panel = panelRef.current;
    const content = contentRef.current;
    if (!overlay || !panel || !content) return;

    gsap.set(overlay, { autoAlpha: 0 });
    gsap.set(panel, { yPercent: 6, autoAlpha: 0 });
    const blocks = content.querySelectorAll<HTMLElement>("[data-reveal]");
    gsap.set(blocks, { y: 24, autoAlpha: 0 });

    const tl = gsap.timeline();
    tl.to(overlay, { autoAlpha: 1, duration: 0.5, ease: "power2.out" }, 0);
    tl.to(panel, { yPercent: 0, autoAlpha: 1, duration: 0.85, ease: "expo.out" }, 0.05);
    tl.to(blocks, { y: 0, autoAlpha: 1, duration: 0.7, ease: "power3.out", stagger: 0.06 }, 0.25);
  }, [mounted, detail]);

  const handleClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    const overlay = overlayRef.current;
    const panel = panelRef.current;
    if (!overlay || !panel) {
      onClose();
      setMounted(false);
      closingRef.current = false;
      return;
    }
    const tl = gsap.timeline({
      onComplete: () => {
        document.body.style.overflow = "";
        onClose();
        setMounted(false);
        closingRef.current = false;
      },
    });
    tl.to(panel, { yPercent: 4, autoAlpha: 0, duration: 0.5, ease: "power2.in" }, 0);
    tl.to(overlay, { autoAlpha: 0, duration: 0.55, ease: "power2.inOut" }, 0.1);
  };

  // Safety: whenever the overlay is fully unmounted, ensure body scroll is restored.
  useEffect(() => {
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  useEffect(() => {
    if (!concernId && mounted && !closingRef.current) setMounted(false);
  }, [concernId, mounted]);

  if (!mounted || !detail) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[75] bg-white overflow-y-auto"
      style={{ opacity: 0, visibility: "hidden" }}
    >
      <div className="w-full px-6 md:px-10 py-6 flex items-center justify-between border-b border-gray-100 sticky top-0 bg-white/95 backdrop-blur z-10">
        <div className="flex items-center gap-3 text-ds-caption text-ink-muted">
          <span className="text-brand">Concern Finder</span>
          <span>/</span>
          <span className="text-ink">{detail.eyebrow.replace(/^\d+\s·\s/, "")}</span>
        </div>
        <button
          onClick={handleClose}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
          aria-label="Close"
        >
          <i className="ri-close-line text-xl" />
        </button>
      </div>

      <div ref={panelRef}>
        <div ref={contentRef} className="w-full px-6 md:px-10">
          {/* Hero */}
          <section className="max-w-ds-wide mx-auto pt-12 md:pt-20 pb-space-ds-subsection-divider md:pb-space-ds-subsection-divider-md">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-6" data-reveal>
                <div className="text-ds-eyebrow text-ink-muted mb-space-ds-intro-eyebrow-heading">
                  {detail.eyebrow}
                </div>
                <h1 className="text-ds-feature font-medium text-ink max-w-[560px]">
                  {detail.title}
                </h1>
                <p className="mt-space-ds-intro-heading-body text-ds-body text-ink-muted max-w-[520px]">
                  {detail.lede}
                </p>
                <div className="mt-space-ds-intro-content flex flex-wrap items-center gap-4">
                  <button
                    onClick={onOpenConsult}
                    className="inline-flex items-center gap-2 px-6 h-11 rounded-ds-pill bg-ink text-white text-ds-control hover:bg-brand transition cursor-pointer whitespace-nowrap"
                  >
                    Book a consultation <i className="ri-arrow-right-line" />
                  </button>
                  <a
                    href="#pricing"
                    onClick={handleClose}
                    className="text-ds-control text-ink border-b border-ink pb-0.5 hover:text-brand hover:border-brand transition cursor-pointer"
                  >
                    See indicative pricing
                  </a>
                </div>
              </div>
              <div className="lg:col-span-6" data-reveal>
                <div className="w-full aspect-[8/5] rounded-ds-media overflow-hidden bg-page-cool">
                  <img src={detail.heroImage} alt={detail.title} className="w-full h-full object-cover object-top" />
                </div>
              </div>
            </div>
          </section>

          {/* Overview */}
          <section className="max-w-ds-wide mx-auto pb-space-ds-subsection-divider md:pb-space-ds-subsection-divider-md">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 border-t border-gray-200 pt-10">
              {[
                { label: "Who it's for", value: detail.overview.whoItsFor },
                { label: "Typical timeline", value: detail.overview.typicalTimeline },
                { label: "Our approach", value: detail.overview.approach },
              ].map((row) => (
                <div key={row.label} data-reveal>
                  <div className="text-ds-eyebrow text-brand mb-space-ds-micro-label-value">{row.label}</div>
                  <div className="text-ds-body text-ink">{row.value}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Journey */}
          <section className="max-w-ds-wide mx-auto pb-space-ds-subsection-divider md:pb-space-ds-subsection-divider-md">
            <div className="mb-10" data-reveal>
              <div className="text-ds-eyebrow text-ink-muted mb-space-ds-intro-eyebrow-heading">The Journey</div>
              <h2 className="text-ds-section font-medium text-ink max-w-[520px]">
                What treatment actually looks like.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {detail.journey.map((j) => (
                <div key={j.step} data-reveal className="border-t border-gray-200 pt-6">
                  <div className="text-ds-meta text-brand font-medium">{j.step}</div>
                  <div className="mt-space-ds-micro-label-value text-ds-list-title text-ink">{j.title}</div>
                  <p className="mt-space-ds-tight-stack text-ds-body-sm text-ink-muted">{j.copy}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Options */}
          <section className="max-w-ds-wide mx-auto pb-space-ds-subsection-divider md:pb-space-ds-subsection-divider-md">
            <div className="mb-10" data-reveal>
              <div className="text-ds-eyebrow text-ink-muted mb-space-ds-intro-eyebrow-heading">Your Options</div>
              <h2 className="text-ds-section font-medium text-ink max-w-[520px]">
                Not one path — a few, chosen with you.
              </h2>
            </div>
            <div className="border-t border-gray-200">
              {detail.options.map((o, i) => (
                <div key={o.name} data-reveal className="border-b border-gray-200 grid grid-cols-1 md:grid-cols-12 gap-6 py-space-ds-list-row-lg md:py-space-ds-list-row-lg-md">
                  <div className="md:col-span-1 text-ds-meta text-ink-faint">{String(i + 1).padStart(2, "0")}</div>
                  <div className="md:col-span-4 text-ds-list-title text-ink">{o.name}</div>
                  <div className="md:col-span-3 text-ds-body-sm text-ink-muted">
                    <span className="text-ink-faint">Suits · </span>{o.suits}
                  </div>
                  <div className="md:col-span-2 text-ds-body-sm text-ink-muted">
                    <span className="text-ink-faint">Timeline · </span>{o.timeline}
                  </div>
                  <div className="md:col-span-2 text-ds-body-sm text-ink">{o.note}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Gallery */}
          <section className="max-w-ds-wide mx-auto pb-space-ds-subsection-divider md:pb-space-ds-subsection-divider-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
              {detail.gallery.map((g, i) => (
                <div key={i} data-reveal>
                  <div className="w-full aspect-[4/5] rounded-ds-media overflow-hidden bg-page-cool">
                    <img src={g.image} alt={g.caption} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="mt-4 text-ds-caption text-ink-muted">{g.caption}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Doctor note + FAQ */}
          <section className="max-w-ds-wide mx-auto pb-space-ds-subsection-divider md:pb-space-ds-subsection-divider-md">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-5" data-reveal>
                <div className="text-ds-eyebrow text-brand mb-space-ds-intro-eyebrow-heading">Doctor Note</div>
                <p className="font-serif-italic text-ds-editorial-quote text-ink leading-snug">
                  "{detail.doctorNote}"
                </p>
                <div className="mt-6 text-ds-caption text-ink-muted">— Dr. Ava Nordin</div>
              </div>
              <div className="lg:col-span-7" data-reveal>
                <div className="text-ds-eyebrow text-ink-muted mb-space-ds-intro-eyebrow-heading">Common Questions</div>
                <div className="border-t border-gray-200">
                  {detail.faqs.map((f) => (
                    <div key={f.q} className="border-b border-gray-200 py-space-ds-list-row">
                      <div className="text-ds-list-title text-ink">{f.q}</div>
                      <p className="mt-space-ds-tight-stack text-ds-body-sm text-ink-muted">{f.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="max-w-ds-wide mx-auto pb-space-ds-section-standard md:pb-space-ds-section-standard-md">
            <div data-reveal className="rounded-ds-media bg-page-cool px-8 md:px-12 py-12 md:py-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
              <div className="max-w-[560px]">
                <div className="text-ds-eyebrow text-brand mb-space-ds-intro-eyebrow-heading">Next Step</div>
                <h3 className="text-ds-conversion-heading text-ink font-medium">
                  A quiet consultation, on your timing.
                </h3>
                <p className="mt-4 text-ds-body text-ink-muted">
                  Bring your questions. We'll look, listen and give you a clear, honest direction — no pressure to commit.
                </p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={onOpenConsult}
                  className="inline-flex items-center gap-2 px-6 h-11 rounded-ds-pill bg-ink text-white text-ds-control hover:bg-brand transition cursor-pointer whitespace-nowrap"
                >
                  Book a consultation <i className="ri-arrow-right-line" />
                </button>
                <button
                  onClick={handleClose}
                  className="text-ds-control text-ink border-b border-ink pb-0.5 hover:text-brand hover:border-brand transition cursor-pointer whitespace-nowrap"
                >
                  Back to overview
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}